import { supabase } from "@/infrastructure/supabase/client";
import type {
  B2cActivityRow,
  B2cIterationBranchRow,
  B2cIterationRow,
  B2cRepo,
  B2cStageRow,
  B2cTaskRow,
  B2cWorkstreamGateRow,
  B2cWorkstreamRow,
  IterationStatus,
} from "@/infrastructure/supabase/b2c-board.types";

export interface CreateB2cTaskInput {
  title: string;
  workstream_key: string;
  iteration_id: string;
  sort_order: number;
}

export type B2cTaskPatch = Partial<
  Pick<
    B2cTaskRow,
    | "title"
    | "description"
    | "status"
    | "iteration_id"
    | "acceptance_md"
    | "notes"
    | "branch_name"
    | "pr_url"
    | "priority"
    | "kind"
  >
>;

export type IterationPatch = Partial<
  Pick<
    B2cIterationRow,
    "status" | "goal" | "output_url" | "marathon_started_at" | "marathon_finished_at" | "merged_at"
  >
>;

export type IterationBranchPatch = Partial<
  Pick<B2cIterationBranchRow, "branch" | "pr_url" | "merged_at">
>;

// The owner is free text: workstream owners are team members, not all of whom are
// stakeholders.
export type WorkstreamPatch = Partial<
  Pick<B2cWorkstreamRow, "title" | "owner_name" | "start_date" | "end_date">
>;

export type StagePatch = Partial<Pick<B2cStageRow, "title" | "start_date" | "end_date">>;

const TASK_PAGE = 1000;

// The B2C board's own tables (migration 0138). Stages, workstreams, gates and
// iterations stay far below PostgREST's 1000-row page; tasks are paged.
export const b2cBoardRepository = {
  async listStages(): Promise<B2cStageRow[]> {
    const { data, error } = await supabase
      .from("b2c_stages")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data as B2cStageRow[] | null) ?? [];
  },

  async listWorkstreams(): Promise<B2cWorkstreamRow[]> {
    const { data, error } = await supabase
      .from("b2c_workstreams")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data as B2cWorkstreamRow[] | null) ?? [];
  },

  async listGates(): Promise<B2cWorkstreamGateRow[]> {
    const { data, error } = await supabase.from("b2c_workstream_gates").select("*");
    if (error) throw error;
    return (data as B2cWorkstreamGateRow[] | null) ?? [];
  },

  async listIterations(): Promise<B2cIterationRow[]> {
    const { data, error } = await supabase
      .from("b2c_iterations")
      .select("*")
      .order("workstream_key", { ascending: true })
      .order("number", { ascending: true });
    if (error) throw error;
    return (data as B2cIterationRow[] | null) ?? [];
  },

  async listIterationBranches(): Promise<B2cIterationBranchRow[]> {
    const { data, error } = await supabase
      .from("b2c_iteration_branches")
      .select("*")
      .order("repo", { ascending: true });
    if (error) throw error;
    return (data as B2cIterationBranchRow[] | null) ?? [];
  },

  // Paged: tasks grow with every marathon, and PostgREST silently caps a response
  // at 1000 rows (the MVP board hit exactly that, BF-1031).
  async listTasks(): Promise<B2cTaskRow[]> {
    const rows: B2cTaskRow[] = [];
    for (let from = 0; ; from += TASK_PAGE) {
      const { data, error } = await supabase
        .from("b2c_tasks")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("code", { ascending: true })
        .range(from, from + TASK_PAGE - 1);
      if (error) throw error;
      const batch = (data as B2cTaskRow[] | null) ?? [];
      rows.push(...batch);
      if (batch.length < TASK_PAGE) return rows;
    }
  },

  async updateWorkstream(key: string, patch: WorkstreamPatch): Promise<B2cWorkstreamRow> {
    const { data, error } = await supabase
      .from("b2c_workstreams")
      .update(patch)
      .eq("key", key)
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cWorkstreamRow;
  },

  async updateStage(key: string, patch: StagePatch): Promise<B2cStageRow> {
    const { data, error } = await supabase
      .from("b2c_stages")
      .update(patch)
      .eq("key", key)
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cStageRow;
  },

  async createIteration(
    workstreamKey: string,
    number: number,
    status: IterationStatus = "planned"
  ): Promise<B2cIterationRow> {
    const { data, error } = await supabase
      .from("b2c_iterations")
      .insert({ workstream_key: workstreamKey, number, status })
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cIterationRow;
  },

  async updateIteration(id: string, patch: IterationPatch): Promise<B2cIterationRow> {
    const { data, error } = await supabase
      .from("b2c_iterations")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cIterationRow;
  },

  /** Add or change one repo's branch of an iteration (one row per iteration and repo). */
  async upsertIterationBranch(
    iterationId: string,
    repo: B2cRepo,
    patch: IterationBranchPatch & { branch: string }
  ): Promise<B2cIterationBranchRow> {
    const { data, error } = await supabase
      .from("b2c_iteration_branches")
      .upsert({ iteration_id: iterationId, repo, ...patch }, { onConflict: "iteration_id,repo" })
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cIterationBranchRow;
  },

  async updateIterationBranch(
    id: string,
    patch: IterationBranchPatch
  ): Promise<B2cIterationBranchRow> {
    const { data, error } = await supabase
      .from("b2c_iteration_branches")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cIterationBranchRow;
  },

  // `code` is left out: the database mints B2C-### from its sequence.
  async createTask(input: CreateB2cTaskInput): Promise<B2cTaskRow> {
    const { data, error } = await supabase
      .from("b2c_tasks")
      .insert({
        title: input.title,
        workstream_key: input.workstream_key,
        iteration_id: input.iteration_id,
        sort_order: input.sort_order,
      })
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cTaskRow;
  },

  async updateTask(id: string, patch: B2cTaskPatch): Promise<B2cTaskRow> {
    const { data, error } = await supabase
      .from("b2c_tasks")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cTaskRow;
  },

  /**
   * Pull a backlog task into an iteration. The predicates repeat
   * planBlockedReason so the database, not the cache, has the last word: the
   * panel reads rows up to 30s old, by which time another editor may have
   * planned or closed this one.
   *
   * They state the rule rather than comparing against the row the panel saw —
   * a task that merely moved not-started → blocked is still plannable, and a
   * compare-and-set would refuse it for no reason. `single()` raises on zero
   * rows, so a refusal surfaces as an error without a count check.
   */
  async planTask(id: string, iterationId: string, sortOrder: number): Promise<B2cTaskRow> {
    const { data, error } = await supabase
      .from("b2c_tasks")
      .update({ iteration_id: iterationId, sort_order: sortOrder })
      .eq("id", id)
      .is("iteration_id", null)
      .neq("status", "done")
      .neq("status", "in-review")
      .select("*")
      .single();
    if (error) throw error;
    return data as B2cTaskRow;
  },

  /**
   * The predicates are the guard, not decoration: the panel decides a task is
   * rejectable from cached rows, and the confirmation stays armed on top of
   * that, so by the time this runs another editor may have planned it. They
   * repeat rejectBlockedReason, atomically.
   *
   * PostgREST answers a delete that RLS refuses and one that matches nothing
   * the same way — zero rows, no error — so the returned ids are what tells
   * either from a success.
   */
  async deleteTask(id: string): Promise<void> {
    const { data, error } = await supabase
      .from("b2c_tasks")
      .delete()
      .eq("id", id)
      .is("iteration_id", null)
      .eq("is_e2e_proof", false)
      .neq("status", "done")
      .select("id");
    if (error) throw error;
    if (((data as { id: string }[] | null) ?? []).length === 0) {
      throw new Error(`Task ${id} was not deleted`);
    }
  },

  /**
   * One page of activity, newest first (migration 0145): the whole board, or one
   * task's. Keyset on `seq` — a save writes several rows on one transaction clock.
   */
  async listActivity(opts: {
    taskId?: string;
    cursor: number | null;
    limit: number;
  }): Promise<{ rows: B2cActivityRow[]; nextCursor: number | null }> {
    let query = supabase
      .from("b2c_activity")
      .select("*")
      .order("seq", { ascending: false })
      .limit(opts.limit);
    if (opts.taskId) query = query.eq("task_id", opts.taskId);
    if (opts.cursor !== null) query = query.lt("seq", opts.cursor);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data as B2cActivityRow[] | null) ?? [];
    const last = rows.at(-1);
    return { rows, nextCursor: rows.length === opts.limit && last ? last.seq : null };
  },
};
